import type { NotificationChannel, NotificationTemplateDefinition } from '@qms360/shared';
import { apiFetch } from './api.js';

/** The Notification Center (spec §13.6): a person's own inbox and preferences. */

export interface InboxNotification {
  id: string;
  app: string | null;
  templateKey: string | null;
  subject: string;
  body: string;
  /** An in-app path, e.g. `/qaqc/periods/…`. */
  linkPath: string | null;
  isCritical: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface InboxPage {
  notifications: InboxNotification[];
  nextCursor: string | null;
}

export const fetchInbox = (params: { before?: string | null; unread?: boolean } = {}) => {
  const query = new URLSearchParams();
  if (params.before) query.set('before', params.before);
  if (params.unread) query.set('unread', 'true');
  const suffix = query.toString();
  return apiFetch<InboxPage>(`/notifications${suffix ? `?${suffix}` : ''}`);
};

export const fetchUnreadCount = () => apiFetch<{ unread: number }>('/notifications/unread-count');

export const markNotificationRead = (id: string) =>
  apiFetch<{ readAt: string }>(`/notifications/${id}/read`, { method: 'POST' });

export const markAllNotificationsRead = () =>
  apiFetch<{ marked: number }>('/notifications/read-all', { method: 'POST' });

export interface ChannelPreference {
  channel: NotificationChannel;
  isEnabled: boolean;
  /** False while the channel has no working transport on this server. */
  available: boolean;
}

export interface Preferences {
  preferences: ChannelPreference[];
  /** Messages no preference can silence, by name. */
  criticalAlwaysDelivered: string[];
}

export const fetchPreferences = () => apiFetch<Preferences>('/notifications/preferences');

export const savePreferences = (preferences: { channel: NotificationChannel; isEnabled: boolean }[]) =>
  apiFetch<Preferences>('/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify({ preferences }),
  });

// --- Templates (administrators) ---------------------------------------------

export interface TemplateOverride {
  channel: NotificationChannel;
  subjectEn: string;
  bodyEn: string;
  subjectAr: string | null;
  bodyAr: string | null;
  updatedAt: string;
}

export interface TemplateView extends NotificationTemplateDefinition {
  canEdit: boolean;
  overrides: TemplateOverride[];
}

export const fetchTemplates = () => apiFetch<{ templates: TemplateView[] }>('/admin/notification-templates');

export const saveTemplate = (
  key: string,
  body: { channel: NotificationChannel; subjectEn: string; bodyEn: string; subjectAr?: string | null; bodyAr?: string | null },
) =>
  apiFetch<{ templates: TemplateView[] }>(`/admin/notification-templates/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const revertTemplate = (key: string, channel: NotificationChannel) =>
  apiFetch<{ templates: TemplateView[] }>(
    `/admin/notification-templates/${encodeURIComponent(key)}/${channel}/revert`,
    { method: 'POST' },
  );

// --- Push on this device ------------------------------------------------------

export interface PushInfo {
  available: boolean;
  /** The server's public signing key, which a device subscribes with. */
  publicKey: string | null;
  reason: string | null;
}

export const fetchPushInfo = () => apiFetch<PushInfo>('/notifications/push');

export const savePushSubscription = (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
  apiFetch<{ subscribed: boolean }>('/notifications/push-subscriptions', {
    method: 'POST',
    body: JSON.stringify(subscription),
  });

export const removePushSubscription = (endpoint: string) =>
  apiFetch<void>('/notifications/push-subscriptions', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  });
